import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api';
import { v4 as uuidv4 } from 'uuid';

export interface AuthState {
  token: string | null;
  clientId: string | null;
  source: string | null;
  status: 'idle' | 'loading' | 'loading-authentication' | 'failed' | 'success';
  userId: string | null;
  error?: string | null;
  isAnonymous: boolean;
}

const getSavedToken = (): string | null => {
  const token = sessionStorage.getItem('token');
  if (token) {
    return token;
  }
  return null;
};

const getSavedUserId = (): string | null => {
  const userId = sessionStorage.getItem('userId');
  if (userId) {
    return userId;
  }
  return null;
};

const initialState: AuthState = {
  token: getSavedToken(),
  clientId: null,
  source: null,
  status: 'idle',
  userId: getSavedUserId(),
  isAnonymous: false
};

interface LoginProps {
  email: string;
  password: string;
}

interface RegisterProps {
  email: string;
  password: string;
}

export const getUserByToken = createAsyncThunk(
  'auth/getUserByToken',
  async ({}: any, { getState }: any) => {
    const { data } = await api.get('/account/getUserByToken', {});
    return data;
  }
);

export const anonymousLogin = createAsyncThunk(
  'auth/anonymous-login',
  async () => {
    const identifier = uuidv4();
    const response = await api.post('/account/check/tutorial', { identifier });
    return response.data;
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: LoginProps) => {
    const response = await api.post('/account/login', {
      email_address: email,
      password,
      isSocialLogin: false
    });
    return response.data;
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async ({ email, password }: RegisterProps, { getState }) => {
    const rootState: any = getState();
    const authState: AuthState = rootState.auth;

    const response = await api.post('account/awayz/user', {
      email_address: email,
      password,
      new_user: authState.userId ? false : true,
      _id: authState.userId
    });
    return response.data;
  }
);

export const loginWithGoogle = createAsyncThunk(
  'auth/login/google',
  async (accessToken: string, { getState }) => {
    const rootState: any = getState();
    const authState: AuthState = rootState.auth;
    const { data } = await api.post('/tp/google/get/user', {
      access_token: accessToken,
      user_id: authState.userId
    });
    return data;
  }
);

export const loginWithApple = createAsyncThunk(
  'auth/login/apple',
  async ({ authorization }: any, { getState }) => {
    const rootState: any = getState();
    const authState: AuthState = rootState.auth;
    const { data } = await api.post('/tp/apple/get/user', {
      authorization,
      user_id: authState.userId
    });
    return data;
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetStatus: (state: AuthState) => {
      state.status = 'idle';
    },
    finalizeRegistration: (state: AuthState, action: PayloadAction<any>) => {
      state.token = action.payload.token;
      state.userId = action.payload.userId;
    },
    logout: (state: AuthState) => {
      // Find better way to do this
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('userId');
      state.userId = null;
      state.token = null;
    },
    setClientDetails: (state: AuthState, action: PayloadAction<any>) => {
      state.clientId = action.payload.clientId;
      state.source = action.payload.source;
    },
    setTokenFromApple: (state: AuthState, action: PayloadAction<any>) => {
      state.token = action.payload;

      try {
        window.parent.postMessage(
          { type: 'odynn-token', value: action.payload },
          '*'
        );
      } catch (error) {}
    },
    setTokenFromParent: (state: AuthState, action: PayloadAction<any>) => {
      state.token = action.payload;
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(register.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.status = action.payload.success ? 'success' : 'failed';
        if (!action.payload.success) {
          state.error = action.payload.message;
        }

        if (action.payload.success)
          //analytics
          analytics.identify(action.payload.user._id, {
            email: action.payload.user.email_address,
            isAnonymous: false,
            register: true,
            token: action.payload.token
          });
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed';
        console.log(action);
      })
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = action.payload.success ? 'success' : 'failed';
        if (action.payload.success) {
          state.token = action.payload.data.token;
          state.userId = action.payload.data.user._id;
          state.isAnonymous = false;

          //analytics
          analytics.identify(action.payload.data.user._id, {
            email: action.payload.data.user.email_address,
            isAnonymous: false,
            token: action.payload.data.token
          });

          try {
            window.parent.postMessage(
              { type: 'odynn-token', value: state.token },
              '*'
            );

            sessionStorage.setItem('token', action.payload.data.token);
            sessionStorage.setItem('userId', action.payload.data.user._id);
          } catch (error) {}
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        console.log(action);
      })
      .addCase(getUserByToken.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getUserByToken.fulfilled, (state: any, action: any) => {
        if (action.payload.success) {
          state.status = 'success';
          state.token = action.payload.token;
          state.userId = action.payload.user._id;
          sessionStorage.setItem('token', action.payload.token);
          sessionStorage.setItem('userId', action.payload.user._id);
          state.isAnonymous = false;

          //adds tracking to anonymous users
          analytics.identify(action.payload.user._id, {
            isAnonymous: false,
            token: action.payload.token
          });
        } else {
          state.status = 'failed';
          state.error = action.payload.message;
        }
      })
      .addCase(getUserByToken.rejected, (state, action) => {
        state.status = 'failed';
        console.log(action);
      })

      .addCase(anonymousLogin.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(anonymousLogin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.status = 'success';
          state.token = action.payload.token;
          state.userId = action.payload.user._id;
          state.isAnonymous = true;

          //analytics
          analytics.identify(action.payload.user._id, {
            isAnonymous: true,
            token: action.payload.token
          });

          try {
            sessionStorage.setItem('token', action.payload.token);
            sessionStorage.setItem('userId', action.payload.user._id);
          } catch (error) {}
        } else {
          state.status = 'failed';
          state.error = action.payload.message;
        }
      })
      .addCase(anonymousLogin.rejected, (state, action) => {
        state.status = 'failed';
        console.log(action);
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.status = 'success';
          state.token = action.payload.data.token;
          state.userId = action.payload.data.user._id;

          //analytics
          analytics.identify(action.payload.data.user._id, {
            isAnonymous: false,
            socialLogin: 'Google',
            token: action.payload.data.token
          });

          try {
            window.parent.postMessage(
              { type: 'odynn-token', value: state.token },
              '*'
            );

            sessionStorage.setItem('token', action.payload.data.token);
            sessionStorage.setItem('userId', action.payload.data.user._id);
          } catch (error) {}
        } else {
          state.status = 'failed';
          state.error = action.payload.message;
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action: any) => {
        state.status = 'failed';
        state.error = action.payload.message;
      })
      .addCase(loginWithApple.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loginWithApple.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.status = 'success';
          state.token = action.payload.data.token;
          state.userId = action.payload.data.user._id;

          //analytics
          analytics.identify(action.payload.data.user._id, {
            isAnonymous: false,
            socialLogin: 'Apple',
            token: action.payload.data.token
          });

          try {
            window.parent.postMessage(
              { type: 'odynn-token', value: state.token },
              '*'
            );

            sessionStorage.setItem('token', action.payload.data.token);
            sessionStorage.setItem('userId', action.payload.data.user._id);
          } catch (error) {}
        } else {
          state.status = 'failed';
          state.error = action.payload.message;
        }
      })
      .addCase(loginWithApple.rejected, (state, action: any) => {
        state.status = 'failed';
        state.error = action.payload.message;
      });
  }
});

export const {
  logout,
  resetStatus,
  finalizeRegistration,
  setClientDetails,
  setTokenFromApple,
  setTokenFromParent
} = authSlice.actions;

export default authSlice.reducer;
